import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Link2, Loader2, Copy, CheckCircle2, Calendar, Lock } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { DashboardItinerary } from "@/lib/models/dashboard";
import { useState } from "react";
import { toast } from "@/hooks/useToast";

interface ShareLinkStep3GenerateProps {
  itinerary: DashboardItinerary;
  linkPassword: string;
  linkExpiry: string | null;
  onGenerate: () => void;
  isGenerating: boolean;
  sharedLink: { shareUrl: string } | null;
}

export const ShareLinkStep3Generate = ({
  itinerary,
  linkPassword,
  linkExpiry,
  onGenerate,
  isGenerating,
  sharedLink,
}: ShareLinkStep3GenerateProps) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    if (!sharedLink) return;
    navigator.clipboard.writeText(sharedLink.shareUrl);
    setCopied(true);
    toast({ variant: 'info',
      title: t('common.copied'),
      description: t('itinerary.linkCopied'),
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const getExpiryText = () => {
    if (!linkExpiry || linkExpiry === 'none') {
      return t('itinerary.noExpiry');
    }
    const days = parseInt(linkExpiry, 10);
    return `${days} ${days === 1 ? t('itinerary.day') : t('itinerary.days')}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {sharedLink ? t('itinerary.linkReady') : t('itinerary.readyToGenerate')}
        </h3>
        <p className="text-sm text-gray-600">
          {sharedLink
            ? t('itinerary.linkReadyDescription')
            : t('itinerary.reviewAndGenerate')}
        </p>
      </div>

      {/* Summary Card */}
      {!sharedLink && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">
              {t('common.itinerary')}
            </p>
            <p className="text-sm font-semibold text-gray-900 mt-1">
              {itinerary.title}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t">
            <div className="flex items-start gap-2">
              <Lock className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-gray-500">
                  {t('itinerary.password')}
                </p>
                <p className="text-sm text-gray-900">
                  {linkPassword ? '••••••••' : t('common.none')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-gray-500">
                  {t('itinerary.expiry')}
                </p>
                <p className="text-sm text-gray-900">{getExpiryText()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Display (after generation) */}
      {sharedLink && (
        <div className="space-y-4">
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-sm text-green-800">
              {t('itinerary.linkGeneratedSuccess')}
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <Label className="text-xs font-medium text-gray-500 uppercase mb-2 block">
              {t('itinerary.shareableLink')}
            </Label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-sm font-mono text-gray-900 overflow-x-auto">
                {sharedLink.shareUrl}
              </div>
              <Button
                onClick={handleCopyLink}
                variant="outline"
                size="sm"
                className="flex-shrink-0"
              >
                {copied ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {t('common.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    {t('common.copy')}
                  </>
                )}
              </Button>
            </div>

            {linkPassword && (
              <Alert className="mt-4 bg-yellow-50 border-yellow-200">
                <Info className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-sm text-yellow-800">
                  {t('itinerary.passwordProtectedReminder')}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      )}

      {/* Generate Button */}
      {!sharedLink && (
        <div className="flex justify-center pt-4">
          <Button
            onClick={onGenerate}
            disabled={isGenerating}
            size="lg"
            className="min-w-[200px]"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {t('itinerary.generating')}
              </>
            ) : (
              <>
                <Link2 className="h-5 w-5 mr-2" />
                {t('itinerary.generateLink')}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
